import { useMemo } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { useAllIndicators } from '../../../../hooks/useIndicators';
import {
  catalogConfigSelector,
  treeIdByCatalogIdSelector,
} from '../../../../store/selectors/tree/data';
import { destructId, hasLoadMoreItems } from '../../../../utils/tree/treeNode';
import ActionsDirective from '../ActionsDirective';
import LoadMoreItems from '../LoadMoreItems';
import Title from '../Title';
import { selectSortedCatalogs } from '../../../../store/slices/treeSort';
import { selectIsGlobalMarket } from '../../../../store/slices/intl';
import Badge from '../../../StatusBadges/Badge';
import { autoCategorised } from '../../../StatusBadges/badgeConfig';

import styles from './Category.module.scss';
import { InvalidImageBadge } from '../../../StatusBadges/InvalidImageBadge';
import type { NodeBase } from '../../../../entities/Node';
import type { RootState } from '../../../../store';
import StatusBadges from '../../../StatusBadges';
import { MISSING_ITEMS_CATALOG } from '../../../../utils/catalog/missingitems';
import { HoverActions } from '../../HoverActions/HoverActions';
import { useHoverActions } from '../../HoverActions/useHoverActions';

const Category = ({ node }: { node: NodeBase }) => {
  const isGlobalMarket = useSelector(selectIsGlobalMarket);
  const { catalogId, category, title, parentId, level } = node;
  const isMissing = node.catalogId === MISSING_ITEMS_CATALOG.catalogId;
  const {
    categoryId = '',
    childCategoryCount,
    childItemCount,
    autoCategorisable = false,
  } = category || {};

  const tree = useSelector((state: RootState) =>
    treeIdByCatalogIdSelector(state, catalogId),
  );

  const { allowRanking, allowRankingLevels } = useSelector(
    (state: RootState) =>
      catalogConfigSelector(state, catalogId) as {
        allowRanking: boolean;
        allowRankingLevels: number[];
      },
  );

  // Check if ranking (drag-drop) is allowed for this node's level
  const isDraggable = useMemo(() => {
    if (allowRankingLevels.length > 0) {
      return allowRankingLevels.includes(level);
    }
    return allowRanking;
  }, [allowRanking, allowRankingLevels, level]);

  const sortedCatalogs = useSelector((state: RootState) =>
    selectSortedCatalogs(state),
  );

  const isRelatedCategory = useMemo(() => {
    const mainParentCategoryId = node.category?.mainParentCategoryId;
    return (
      (mainParentCategoryId &&
        mainParentCategoryId !== destructId(node?.parentId).value) ||
      false
    );
  }, [node]);

  const isNew = useMemo(() => !categoryId && !node.title, [categoryId, node.title]);
  const loadMore = useMemo(() => hasLoadMoreItems(node), [node]);
  const memoTitle = useMemo(() => {
    if (!childItemCount) {
      return title;
    }
    if (isMissing) {
      return (
        <>
          {title}
          <span style={{ color: 'rgb(var(--colour-semantic-negative))' }}>
            {` (${childItemCount})`}
          </span>
        </>
      );
    }
    return `${title} (${childItemCount})`;
  }, [childItemCount, isMissing, title]);
  const { badges, summaries } = useAllIndicators(catalogId, category);
  const sortedCatalog = sortedCatalogs[catalogId];
  const sortedParent = sortedCatalog && sortedCatalog[parentId];
  const hasChildren = !!childCategoryCount || !!childItemCount;
  const showHoverActions = useHoverActions(node);

  return (
    <ActionsDirective node={node}>
      <div
        className={cx(
          styles.titleWrapper,
          {
            [styles.relatedCategory]: isRelatedCategory,
          },
          {
            [styles.new]: isNew,
          },
          {
            [styles.isSorted]: sortedParent && sortedParent[categoryId],
          },
          {
            [styles.draggable]: isDraggable,
          },
        )}
      >
        <StatusBadges className="" badges={badges} summaries={summaries} compact />
        {node.invalidCategoryImage && hasChildren && <InvalidImageBadge compact />}
        {isGlobalMarket && autoCategorisable && (
          <Badge {...autoCategorised} compact />
        )}
        <Title title={memoTitle} />
      </div>
      {loadMore && <LoadMoreItems node={node} tree={tree || ''} />}
      {showHoverActions && <HoverActions node={node} />}
    </ActionsDirective>
  );
};

export default Category;
